import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks';
import styles from './Sidebar.module.css';

/**
 * Sidebar Navigation
 * 
 * Main navigation sidebar for the admin dashboard.
 */

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const syncNavItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/analytics', label: 'Analytics', icon: '📈' },
  { path: '/events', label: 'Sync Events', icon: '🔄' },
  { path: '/sessions', label: 'Sessions', icon: '📦' },
  { path: '/users', label: 'Users', icon: '👤' },
  { path: '/devices', label: 'Devices', icon: '📱' },
  { path: '/corrupted', label: 'Corrupted', icon: '⚠️' },
  { path: '/deduplication', label: 'Deduplication', icon: '🔀' },
  { path: '/conflicts', label: 'Conflicts', icon: '⚡' },
  { path: '/device-link-conflicts', label: 'ID Collisions', icon: '⚠️' },
];

const accountNavItems: NavItem[] = [
  { path: '/accounts/overview', label: 'Studio Overview', icon: '📊' },
  { path: '/accounts/growth', label: 'Growth', icon: '📈' },
  { path: '/accounts', label: 'User Accounts', icon: '👥' },
];

const featureNavItems: NavItem[] = [
  { path: '/features', label: 'Feature Analytics', icon: '🧪' },
  { path: '/platforms', label: 'Platform Analytics', icon: '📱' },
  { path: '/desktop', label: 'Desktop App', icon: '🖥️' },
];

const educationNavItems: NavItem[] = [
  { path: '/education/inquiries', label: 'Inquiries', icon: '📬' },
  { path: '/education/analytics', label: 'Analytics', icon: '🎓' },
];

const marketingNavItems: NavItem[] = [
  { path: '/web-analytics', label: 'Web Traffic', icon: '🌐' },
  { path: '/email-marketing', label: 'Email Marketing', icon: '✉️' },
  { path: '/abandoned-checkout', label: 'Abandoned Checkout', icon: '🛒' },
];

const commerceNavItems: NavItem[] = [
  { path: '/commerce', label: 'Shopify Analytics', icon: '🛒' },
  { path: '/funnel', label: 'Checkout Funnel', icon: '🔻' },
  { path: '/revenue', label: 'Monthly Revenue', icon: '💰' },
];

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  { label: 'Sync Events', items: syncNavItems },
  { label: 'User Accounts', items: accountNavItems },
  { label: 'Product', items: featureNavItems },
  { label: 'Education', items: educationNavItems },
  { label: 'Marketing', items: marketingNavItems },
  { label: 'Commerce', items: commerceNavItems },
];

// Support users see only the User Accounts list. Kept in sync with the backend
// IsSupportOrAdmin whitelist; the backend is what actually enforces this.
const supportNavSections: NavSection[] = [
  {
    label: 'User Accounts',
    items: accountNavItems.filter((item) => item.path === '/accounts'),
  },
];

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isSupportOnly } = useAuth();
  const sections = isSupportOnly ? supportNavSections : navSections;

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
      <div className={styles.logo}>
        <span className={styles.logoIcon}>⚡</span>
        <span className={styles.logoText}>BYOK Admin</span>
      </div>

      <nav className={styles.nav}>
        {sections.map((section) => (
          <div key={section.label} className={styles.navSection}>
            <span className={styles.navSectionLabel}>{section.label}</span>
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                }
                end={section.label !== 'Sync Events' || item.path === '/'}
                onClick={onClose}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                <span className={styles.navLabel}>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className={styles.footer}>
        <span className={styles.version}>v1.0.0</span>
      </div>
    </aside>
  );
}

